<template>
    <modal :parent="configurationStore.appContainerSelector" @opened="selectorOpened" @closed="selectorClosed">
        <template #opener="{ open }">
            <slot name="opener" :open="open" :quickSelect="quickSelect" :focus="focusOpener" />
        </template>

        <template #header="{ close }">
            <slot name="header" :close="close" />
            <search-field v-if="showSearch" :value="filter" :focus="focusSearchField" @updated="search" />
        </template>

        <template #default="{ close }">
            <list-select name="currency-select" :selected="[state.value]" :disabled="disabledValues" @updated="(data) => updateCurrency(data, close)">
                <template v-for="currency of filteredCurrencies" #[getCurrencyCode(currency)]>
                    <list-select-item :icon="getCurrencyCode(currency)" directory="currencies">
                        {{ getCurrencyName(currency) }}
                        <template #sub>
                            {{ !disabledValues.includes(getCurrencyCode(currency)) ? getCurrencyCode(currency) : 'Currently unavailable' }}
                        </template>
                    </list-select-item>
                </template>
            </list-select>
        </template>
    </modal>
</template>

<script lang="ts" setup>
import { ListSelect, ListSelectItem } from '@btcdirect/components';
import SearchField from '@btcdirect/shared/src/components/form/fields/search-field.vue';
import { CurrencyPair, useConfigurationStore, useCurrenciesStore } from '@btcdirect/stores';
import { Modal, ValidatedStringArrayFieldData, ValidatedStringFieldData } from 'out-of-the-vue';
import { computed, onMounted, reactive, ref, watch } from 'vue';

const emit = defineEmits<{ (event: 'created' | 'updated', data: ValidatedStringFieldData): void }>();

const props = defineProps({
    value: {
        type: String,
        required: false,
        default: null
    },
    allowedValues: {
        type: Array as () => string[],
        required: false,
        default: null
    },
    disabledValues: {
        type: Array as () => string[],
        required: false,
        default: null
    },
    type: {
        type: String as () => 'fiat' | 'crypto',
        required: false,
        default: 'crypto'
    },
    showSearch: {
        type: Boolean,
        required: false,
        default: true
    }
});

const configurationStore = useConfigurationStore();
const currenciesStore = useCurrenciesStore();

const state = reactive<ValidatedStringFieldData>({
    name: 'currency-select',
    value: props.value,
    valid: !props.value,
    failed: !props.value ? ['required'] : []
});

const currencyType = props.type === 'fiat' ? 'quoteCurrency' : 'baseCurrency';

const filter = ref<string>('');
const previousFilter = ref<string>('');

const search = (data: ValidatedStringFieldData): void => {
    filter.value = !!data.value ? data.value.trim() : null;
};

watch(
    () => props.value,
    (received: string) => {
        selectCurrency(received);
    }
);

const filteredCurrencies = computed<CurrencyPair[]>(() => {
    const search = filter.value?.toUpperCase();

    const allowed =
        !props.allowedValues || !props.allowedValues.length
            ? currenciesStore.currencyPairs
            : currenciesStore.currencyPairs.filter((currency) => props.allowedValues.includes(getCurrencyCode(currency)));

    const filtered = !search
        ? allowed
        : allowed.filter((currency) => getCurrencyName(currency).toUpperCase().includes(search) || getCurrencyCode(currency).includes(search));

    if (!currenciesStore.selectedCurrencyPair) {
        return filtered;
    }

    return [currenciesStore.selectedCurrencyPair, ...filtered.filter((pair) => pair.currencyPair !== currenciesStore.selectedCurrencyPair.currencyPair)];
});

const focusOpener = ref<boolean>(false);
const focusSearchField = ref<boolean>(false);

const selectorOpened = (): void => {
    focusOpener.value = false;

    setTimeout(() => {
        filter.value = previousFilter.value;
        previousFilter.value = null;
    }, 50);

    setTimeout(() => {
        focusSearchField.value = true;
    }, 550);
};

const selectorClosed = (): void => {
    focusSearchField.value = false;
    focusOpener.value = true;

    previousFilter.value = filter.value;
    filter.value = null;
};

const quickSelect = (event: KeyboardEvent): void => {
    const letter = event.key.toUpperCase();
    if (!letter.match(/[A-Z]/g)) {
        return;
    }

    const found = currenciesStore.currencyPairs.filter((currency) => getCurrencyName(currency).startsWith(letter));
    if (!found || !found.length) {
        return;
    }

    const selectedStartingLetter = getCurrencyName(currenciesStore.selectedCurrencyPair) || null;
    if (found.length === 1 || !selectedStartingLetter || !selectedStartingLetter.startsWith(letter)) {
        selectCurrency(getCurrencyCode(found[0]));
        return;
    }

    const index = found.findIndex((currency) => getCurrencyCode(currency) === getCurrencyCode(currenciesStore.selectedCurrencyPair));
    if (index === found.length - 1) {
        selectCurrency(getCurrencyCode(found[0]));
        return;
    }

    selectCurrency(getCurrencyCode(found[index + 1]));
};

const getCurrencyCode = (currency: CurrencyPair) => currency[currencyType].code;
const getCurrencyName = (currency: CurrencyPair) => currency[currencyType].name;

const updateCurrency = (data: ValidatedStringArrayFieldData, close: () => void): void => {
    selectCurrency(data.value[0]);
    close();
};

const selectCurrency = (code: string): void => {
    if (code === state.value) {
        return;
    }

    state.value = code;
    state.valid = !!code;
    state.failed = !code ? ['required'] : [];

    props.type === 'fiat' ? currenciesStore.setFiatCurrency(state.value) : currenciesStore.setCryptoCurrency(state.value);
    emit('updated', state);
};

onMounted(() => {
    state.valid = !!props.value;
    state.failed = !props.value ? ['required'] : [];
    emit('created', state);
});
</script>
